import { DeviceHealth } from '../types';
import { el, addClass, query } from '../util/dom';
import { formatDateTime, formatRelativeTime } from '../util/format';

// Color schemes for status badges
const STATUS_COLORS = {
  'IN_SYNC': { badge: '#067647', bg: '#ecfdf3', label: '✅ In Sync' },
  'STALE': { badge: '#f57f17', bg: '#fff8e1', label: '⚠️ Stale' },
  'ERROR': { badge: '#d32f2f', bg: '#ffebee', label: '❌ Error' },
  'UNKNOWN': { badge: '#999', bg: '#f5f5f5', label: '❓ Unknown' },
};

const CONNECTIVITY_COLORS = {
  'ONLINE': { badge: '#067647', text: '🟢 Online' },
  'OFFLINE': { badge: '#f57f17', text: '🟡 Offline' },
};

export function createHealthPanel(health: DeviceHealth): HTMLElement {
  const container = el('div', { class: 'device-health-panel' });

  // Header row: Device name + management role
  const header = el('div', { class: 'health-header' });
  const deviceName = el('div', { class: 'health-device-name' }, health.device.name || 'Unnamed Device');
  const role = el('div', { class: 'health-role' }, getRoleLabel(health.device.role));
  header.appendChild(deviceName);
  header.appendChild(role);
  container.appendChild(header);

  // Status row: Connectivity + last seen
  const statusRow = el('div', { class: 'health-status-row' });
  const connectivity = CONNECTIVITY_COLORS[health.connectivity.status];
  const connectStatus = el('div', { class: 'health-status-badge' });
  connectStatus.innerHTML = connectivity.text;
  connectStatus.style.color = connectivity.badge;
  statusRow.appendChild(connectStatus);

  const lastSeen = el('div', { class: 'health-last-seen' });
  lastSeen.textContent = `Last: ${formatRelativeTime(health.connectivity.lastSeen)}`;
  statusRow.appendChild(lastSeen);
  container.appendChild(statusRow);

  // Policy sync row
  if (health.desired && health.applied) {
    const syncRow = el('div', { class: 'health-sync-row' });

    const desiredLabel = el('div', { class: 'health-label' }, 'Desired policy version: ');
    const desiredVersion = el('span', { class: 'health-version' }, String(health.desired.version));
    desiredLabel.appendChild(desiredVersion);
    syncRow.appendChild(desiredLabel);

    const appliedLabel = el('div', { class: 'health-label' }, 'Applied policy version: ');
    const appliedVersion = el('span', { class: 'health-version' }, String(health.applied.appliedVersion));
    appliedLabel.appendChild(appliedVersion);

    const statusBadge = el('span', { class: 'health-compliance-badge' });
    const statusColor = STATUS_COLORS[health.applied.complianceStatus];
    statusBadge.textContent = statusColor.label;
    statusBadge.style.color = statusColor.badge;
    statusBadge.style.backgroundColor = statusColor.bg;
    appliedLabel.appendChild(statusBadge);
    syncRow.appendChild(appliedLabel);

    container.appendChild(syncRow);
  }

  // Capabilities row
  if (health.applied?.capabilities) {
    const capsRow = el('div', { class: 'health-capabilities-row' });
    const caps = health.applied.capabilities;

    const capsList = el('div', { class: 'health-capabilities' });
    if (caps.deviceOwnerActive !== undefined) {
      const cap = el('div', { class: 'health-capability' });
      cap.innerHTML = `Device Owner: ${caps.deviceOwnerActive ? '✅ Active' : '❌ Not available'}`;
      capsList.appendChild(cap);
    }
    if (caps.vpnActive !== undefined) {
      const cap = el('div', { class: 'health-capability' });
      cap.innerHTML = `VPN: ${caps.vpnActive ? '✅ Active' : '❌ Not active'}`;
      capsList.appendChild(cap);
    }

    capsRow.appendChild(capsList);
    container.appendChild(capsRow);
  }

  // Warnings section
  if (health.warnings.length > 0) {
    const warningsSection = el('div', { class: 'health-warnings' });
    const warningsTitle = el('div', { class: 'health-warnings-title' }, 'Warnings:');
    warningsSection.appendChild(warningsTitle);

    const warningsList = el('ul', { class: 'health-warnings-list' });
    health.warnings.forEach(warning => {
      const item = el('li', { class: 'health-warning-item' }, warning);
      warningsList.appendChild(item);
    });
    warningsSection.appendChild(warningsList);
    container.appendChild(warningsSection);
  } else {
    const noWarnings = el('div', { class: 'health-no-warnings' }, 'No warnings');
    container.appendChild(noWarnings);
  }

  return container;
}

function getRoleLabel(role?: string): string {
  switch (role) {
    case 'PARENT': return 'Device Owner (Full)';
    case 'COMPANION': return 'Companion (Limited)';
    case 'CHILD': return 'Child Device';
    default: return 'Unassigned';
  }
}

// Styles for health panel (add to global stylesheet)
export const HEALTH_PANEL_STYLES = `
.device-health-panel {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.6;
}

.health-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
}

.health-device-name {
  font-weight: 600;
  font-size: 16px;
  color: var(--text);
}

.health-role {
  font-size: 12px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.health-status-row,
.health-sync-row {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
}

.health-status-badge {
  font-weight: 500;
  white-space: nowrap;
}

.health-last-seen {
  color: var(--text-muted);
  font-size: 13px;
}

.health-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
}

.health-version {
  font-family: monospace;
  background: var(--bg);
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 600;
}

.health-compliance-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  margin-left: auto;
}

.health-capabilities-row {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
}

.health-capabilities {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.health-capability {
  font-size: 13px;
  color: var(--text);
  padding: 4px;
}

.health-warnings {
  margin-top: 12px;
  padding: 10px;
  background: var(--danger-light);
  border-left: 3px solid var(--danger);
  border-radius: 4px;
}

.health-warnings-title {
  font-weight: 600;
  color: var(--danger);
  margin-bottom: 8px;
}

.health-warnings-list {
  list-style: none;
  margin: 0;
  padding: 0;
  color: var(--text);
}

.health-warning-item {
  font-size: 13px;
  padding: 4px 0;
  line-height: 1.4;
}

.health-warning-item::before {
  content: "• ";
  color: var(--danger);
  font-weight: bold;
  margin-right: 6px;
}

.health-no-warnings {
  margin-top: 12px;
  padding: 8px;
  background: var(--success-light);
  color: var(--success);
  border-radius: 4px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
}
`;
